import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router';
import useDisplayMessage from 'components/messages/useMessage';
import useAuth from 'hooks/useAuth';
import { Organisation } from '../../../@types/organization';
import { loginRequest } from 'authConfig';
import { PATH_AUTH } from 'routes/paths';
import useCurrentLoginMethod from './useCurrentLoginMethod';
import { useTranslator } from 'translation/useTranslator';

type UseMicrosoftLoginProps = {
  userOrganisations: Organisation[];
  setUserOrganisations: (organisations: Organisation[]) => void;
};

const useMicrosoftLogin = ({
  userOrganisations,
  setUserOrganisations
}: UseMicrosoftLoginProps): {
  handleLoginWithMicrosoft: () => Promise<void>;
  isMicrosoftSsoLoading: boolean;
} => {
  const translator = useTranslator();
  const loginMethod = useCurrentLoginMethod();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const displayMessage = useDisplayMessage();
  const { loginExternal } = useAuth();
  const tokenAcquiredRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLoginWithMicrosoft = async () => {
    tokenAcquiredRef.current = false;
    await instance.handleRedirectPromise();
    instance.setActiveAccount(null);
    try {
      await instance.loginPopup(loginRequest);
    } catch {
      await instance.loginRedirect(loginRequest);
    }
  };

  const acquireTokenWithMicrosoft = useCallback(
    async (idToken: string) => {
      if (tokenAcquiredRef.current) return;
      tokenAcquiredRef.current = true;
      await instance.handleRedirectPromise();

      const account = instance.getActiveAccount();

      try {
        const tenants = await loginExternal(idToken);

        if (tenants.length > 0) {
          setUserOrganisations(tenants);
        }
      } catch {
        tokenAcquiredRef.current = false;
        displayMessage.error({
          message: {
            title: translator.userDoesNotHaveAnAccountTitle(),
            description: translator.userDoesNotHaveAnAccountDescription(account?.username ?? '')
          }
        });
        navigate(PATH_AUTH.login);
      }
    },
    [instance, displayMessage, loginExternal, navigate, setUserOrganisations, translator]
  );

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      if (
        (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.LOGIN_SUCCESS) &&
        instance.getActiveAccount() !== null &&
        event.payload.idToken !== null
      ) {
        const idToken = event.payload.idToken;
        acquireTokenWithMicrosoft(idToken);
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [acquireTokenWithMicrosoft, instance]);

  useEffect(() => {
    setIsLoading(
      inProgress !== InteractionStatus.None ||
        (loginMethod === 'microsoft' && !userOrganisations.length)
    );
  }, [inProgress, loginMethod, userOrganisations.length]);

  return {
    handleLoginWithMicrosoft,
    isMicrosoftSsoLoading: isLoading
  };
};

export default useMicrosoftLogin;
